import { useAuthStore } from "@/stores/auth-store";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

const API_BASE = "/api";

async function fetchMe() {
	const res = await fetch(`${API_BASE}/auth/me`, { credentials: "include" });
	if (!res.ok) return null;
	return res.json();
}

async function postLogin(login: string, password: string) {
	const res = await fetch(`${API_BASE}/auth/login`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ login, password }),
	});
	if (!res.ok) {
		const data = await res.json();
		throw new Error(data.message || "Login failed");
	}
	return res.json();
}

async function postLogout() {
	const res = await fetch(`${API_BASE}/auth/logout`, {
		method: "POST",
		credentials: "include",
	});
	if (!res.ok) throw new Error("Logout failed");
	return res.json();
}

async function postChangePassword(currentPassword: string, newPassword: string) {
	const res = await fetch(`${API_BASE}/auth/change-password`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		credentials: "include",
		body: JSON.stringify({ currentPassword, newPassword }),
	});
	if (!res.ok) {
		const data = await res.json();
		throw new Error(data.message || "Change password failed");
	}
	return res.json();
}

export function useAuth() {
	const queryClient = useQueryClient();
	const { setUser, clearUser } = useAuthStore();

	const meQuery = useQuery({
		queryKey: ["auth", "me"],
		queryFn: fetchMe,
		retry: false,
		staleTime: 60_000,
	});

	// Sync query result to Zustand (in useEffect to avoid render-loop)
	const meData = meQuery.data;
	const meIsError = meQuery.isError;
	const meIsSuccess = meQuery.isSuccess;

	useEffect(() => {
		if (meData?.user) {
			setUser(meData.user, meData.mustChangePassword);
		} else if (meIsError || (meIsSuccess && !meData)) {
			clearUser();
		}
	}, [meData, meIsError, meIsSuccess, setUser, clearUser]);

	const loginMutation = useMutation({
		mutationFn: ({ login, password }: { login: string; password: string }) =>
			postLogin(login, password),
		onSuccess: (data) => {
			setUser(data.user, data.mustChangePassword);
			queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
		},
	});

	const logoutMutation = useMutation({
		mutationFn: postLogout,
		onSuccess: () => {
			clearUser();
			queryClient.clear();
		},
	});

	const changePasswordMutation = useMutation({
		mutationFn: ({
			currentPassword,
			newPassword,
		}: { currentPassword: string; newPassword: string }) =>
			postChangePassword(currentPassword, newPassword),
		onSuccess: () => {
			// Refresh /me to get updated mustChangePassword
			queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
		},
	});

	return {
		meQuery,
		loginMutation,
		logoutMutation,
		changePasswordMutation,
		isLoading: meQuery.isLoading,
	};
}
